import React, { useState, useCallback } from "react";
import { Plus, Minus, ChevronDown } from "lucide-react";

/* --------------------------------------------------------------------- */
/* Small helpers                                                          */
/* --------------------------------------------------------------------- */

const Diamond = () => (
  <span className="mt-2 block w-1.5 h-1.5 border border-[#c9a961] rotate-45 shrink-0" aria-hidden="true" />
);

const Book = ({ title, author }) => (
  <div className="py-1.5">
    <div className="font-display italic text-[#1e2952] text-[16px] md:text-[17px] leading-snug">{title}</div>
    {author && (
      <div className="font-sans-nav text-[10.5px] tracking-[0.2em] uppercase text-[#8a7a4e] mt-0.5">
        {author}
      </div>
    )}
  </div>
);

const BookList = ({ visible = [], hidden = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const hasHidden = hidden && hidden.length > 0;
  return (
    <div className="mt-5">
      <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-3">
        Sample Booklist
      </div>
      <div className="h-px w-10 bg-[#c9a961]/70 mb-4" />
      <div className="space-y-1.5">
        {visible.map((b) => (
          <Book key={`v-${b.title}`} {...b} />
        ))}
        {expanded && hasHidden && (
          <div className="mt-3 pt-3 border-t border-dotted border-[#c9a961]/40 space-y-1.5">
            {hidden.map((b) => (
              <Book key={`h-${b.title}`} {...b} />
            ))}
          </div>
        )}
      </div>
      {hasHidden && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-5 font-sans-nav text-[10.5px] tracking-[0.28em] uppercase text-[#1e2952] hover:text-[#c9a961] transition-colors inline-flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#c9a961] focus:ring-offset-2 focus:ring-offset-[#fbf7ee] rounded-[2px] px-1 py-1"
        >
          {expanded ? "Hide Sample Booklist" : "View Sample Booklist"}
          <span className={`transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true">
            <ChevronDown size={14} strokeWidth={1.75} />
          </span>
        </button>
      )}
    </div>
  );
};

const SectionEyebrow = ({ children }) => (
  <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
    {children}
  </div>
);

/* --------------------------------------------------------------------- */
/* Panel variants — each renders one shape of the curriculum data         */
/* --------------------------------------------------------------------- */

const HumanitiesFormPanel = ({ section }) => {
  const hasRotation = section.rotationCycles && section.rotationCycles.length > 0;
  const rotationCols = hasRotation && section.rotationCycles.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3";
  return (
    <div className="pt-4 md:pt-6 pb-10 md:pb-12">
      {section.subtitle && (
        <div className="font-display italic text-[#c9a961] text-[16px] md:text-[17px] tracking-wide mb-4">
          {section.subtitle}
        </div>
      )}

      {section.formDescription && (
        <p className="font-serif-body text-[16px] md:text-[16.5px] leading-[1.85] text-[#3a466e] max-w-[880px] mb-10">
          {section.formDescription}
        </p>
      )}

      {hasRotation && (
        <div className="pt-8 border-t border-[#eae1c4]">
          {section.cycleLabel && (
            <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-6">
              {section.cycleLabel}
            </div>
          )}
          <div className={`grid grid-cols-1 ${rotationCols} gap-y-12 md:gap-x-10 lg:gap-x-14`}>
            {section.rotationCycles.map((c, i) => (
              <div key={c.year} className={i > 0 ? "md:border-l md:border-[#c9a961]/25 md:pl-10 lg:pl-14" : ""}>
                <div className="font-sans-nav text-[10.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-2">
                  {c.year}
                </div>
                <div className="font-display italic text-[#1e2952] text-[17px] md:text-[18px] leading-snug mb-3" style={{ fontWeight: 500 }}>
                  {c.cycle}
                </div>
                <div className="h-px w-10 bg-[#c9a961] mb-5"></div>
                {c.books && c.books.length > 0 && (
                  <>
                    <SectionEyebrow>Sample Booklist</SectionEyebrow>
                    <div className="space-y-1">
                      {c.books.map((b) => (
                        <Book key={b.title} {...b} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasRotation && section.sampleBooks && section.sampleBooks.length > 0 && (
        <div className="pt-8 border-t border-[#eae1c4]">
          <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-3">
            Sample Booklist
          </div>
          <div className="h-px w-10 bg-[#c9a961] mb-5"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-0">
            {section.sampleBooks.map((b) => (
              <Book key={b.title} {...b} />
            ))}
          </div>
          {section.sampleBooksNote && (
            <p className="font-serif-body italic text-[13.5px] leading-[1.7] text-[#8a7a4e] max-w-[880px] mt-6">
              {section.sampleBooksNote}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const RotationYearsPanel = ({ section }) => {
  const gridCols = section.rotationYears.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3";
  return (
    <div className="pt-4 md:pt-6 pb-10 md:pb-12">
      {section.subtitle && (
        <div className="font-display italic text-[#c9a961] text-[16px] md:text-[17px] tracking-wide mb-2">
          {section.subtitle}
        </div>
      )}

      {section.subjectAreas && section.subjectAreas.length > 0 && (
        <div className="font-sans-nav text-[10.5px] tracking-[0.28em] uppercase text-[#8a7a4e] mb-6">
          {section.subjectAreas.join(" · ")}
        </div>
      )}

      {section.formDescription && (
        <p className="font-serif-body text-[16px] md:text-[16.5px] leading-[1.85] text-[#3a466e] max-w-[880px] mb-10">
          {section.formDescription}
        </p>
      )}

      <p className="font-serif-body italic text-[14.5px] leading-[1.7] text-[#8a7a4e] max-w-[880px] mb-10">
        {section.rotationYears.length === 2
          ? "Year A and Year B indicate rotating humanities cycles rather than grade levels. Students enter the cycle wherever it is currently being taught and continue through the rotation as they progress through the Form."
          : "Year A, Year B, and Year C indicate rotating humanities cycles rather than grade levels. Students enter the cycle wherever it is currently being taught and continue through the rotation as they progress through the Form."}
      </p>

      <div className="pt-8 border-t border-[#eae1c4]">
        {section.cycleLabel && (
          <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-6">
            {section.cycleLabel}
          </div>
        )}
        <div className={`grid grid-cols-1 ${gridCols} gap-y-10 md:gap-x-10 lg:gap-x-14`}>
          {section.rotationYears.map((yr, yi) => (
            <div key={yr.year} className={yi > 0 ? "md:border-l md:border-[#c9a961]/25 md:pl-10 lg:pl-14" : ""}>
              <div className="font-sans-nav text-[10.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-2">
                {yr.year}
              </div>
              <div className="h-px w-10 bg-[#c9a961] mb-5"></div>
              <div className="space-y-2">
                {yr.books.map((b) => (
                  <Book key={b.title} {...b} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SampleBooklistPanel = ({ section }) => (
  <div className="pt-4 md:pt-6 pb-10 md:pb-12">
    {section.descriptor && (
      <p className="font-serif-body italic text-[16px] md:text-[17px] leading-[1.8] text-[#3a466e] max-w-[880px] mb-10">
        {section.descriptor}
      </p>
    )}
    <div className="pt-6 border-t border-[#eae1c4]">
      <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-3">
        Partial Booklist
      </div>
      <div className="h-px w-10 bg-[#c9a961] mb-5"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
        {section.sampleBooklist.map((b) => (
          <Book key={b.title} {...b} />
        ))}
      </div>
    </div>
  </div>
);

const SubjectsPanel = ({ section }) => (
  <div className="pt-4 md:pt-6 pb-10 md:pb-12">
    {section.descriptor && (
      <p className="font-serif-body italic text-[16px] md:text-[17px] leading-[1.8] text-[#3a466e] max-w-[880px] mb-10">
        {section.descriptor}
      </p>
    )}
    <div className="divide-y divide-[#eae1c4]">
      {section.subjects.map((sub) => (
        <div key={sub.title} className="py-8 first:pt-0">
          <h4 className="font-display text-[#1e2952] text-[20px] md:text-[22px] leading-tight" style={{ fontWeight: 600 }}>
            {sub.title}
          </h4>
          <div className="h-px w-10 bg-[#c9a961] mt-3 mb-4"></div>
          {sub.description && (
            <p className="font-serif-body text-[15.5px] md:text-[16px] leading-[1.75] text-[#3a466e] mb-5">
              {sub.description}
            </p>
          )}
          {sub.samples && sub.samples.length > 0 && (
            <div>
              <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-3">
                {sub.samplesLabel || "Sample Booklist"}
              </div>
              <div className="space-y-1.5">
                {sub.samples.map((b) => (
                  <Book key={b.title} {...b} />
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const ResourceList = ({ resource }) => {
  if (Array.isArray(resource)) {
    return (
      <ul className="font-display italic text-[#1e2952] text-[18px] md:text-[20px] leading-snug space-y-1 list-none" style={{ fontWeight: 500 }}>
        {resource.map((r) => (
          <li key={r} className="flex items-start gap-2">
            <span className="text-[#c9a961] not-italic">·</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="font-display italic text-[#1e2952] text-[18px] md:text-[20px]" style={{ fontWeight: 500 }}>
      {resource}
    </div>
  );
};

const ProgressionPanel = ({ section }) => {
  const hasProgression = section.progression && section.progression.length > 0;
  return (
    <div className="pt-4 md:pt-6 pb-10 md:pb-12">
      {section.resource && (
        <div className="mb-6">
          <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
            {Array.isArray(section.resource) ? "Curriculum Resources" : "Curriculum Resource"}
          </div>
          <ResourceList resource={section.resource} />
        </div>
      )}
      {hasProgression && (
        <div className="flex flex-col items-start gap-y-2">
          {section.progression.map((step, i) => (
            <div key={step} className="flex items-center gap-3">
              <span className="font-display italic text-[#1e2952] text-[17px] md:text-[19px] leading-snug">
                {step}
              </span>
              {i < section.progression.length - 1 && (
                <span className="text-[#c9a961] font-serif-body text-base md:text-lg leading-none">→</span>
              )}
            </div>
          ))}
        </div>
      )}

      {section.progressionNote && (
        <p className={`${hasProgression ? "mt-6" : "mt-2"} font-serif-body italic text-[#8a7a4e] text-[14.5px] md:text-[15px] leading-[1.75] max-w-[720px]`}>
          {section.progressionNote}
        </p>
      )}

      {section.books && section.books.length > 0 && (
        <div className="mt-8 pt-6 border-t border-dotted border-[#c9a961]/40">
          <BookList visible={section.books} hidden={section.moreBooks} />
        </div>
      )}

      {section.footer && (
        <div className="mt-10 pt-6 border-t border-[#eae1c4]">
          <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
            {section.footer.eyebrow}
          </div>
          <p className="font-serif-body italic text-[#3a466e] text-[15px] md:text-[16px] leading-[1.75]">
            {section.footer.body}
          </p>
        </div>
      )}
    </div>
  );
};

const ResourceListItem = ({ resource }) => {
  const isLink = resource && typeof resource === "object" && resource.href;
  return (
    <li className="flex items-start gap-3">
      <Diamond />
      {isLink ? (
        <a
          href={resource.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#c9a961] hover:text-[#1e2952] underline decoration-[#c9a961]/60 underline-offset-2 transition-colors"
        >
          {resource.name}
        </a>
      ) : (
        <span>{typeof resource === "object" ? resource.name : resource}</span>
      )}
    </li>
  );
};

const LevelRotation = ({ rotation }) => (
  <div className="mt-6 space-y-4">
    <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961]">
      Three-Year Rotation
    </div>
    {rotation.map((r) => (
      <div key={r.year} className="pt-4 border-t border-dotted border-[#c9a961]/40">
        <div className="font-display italic text-[#1e2952] text-[18px] md:text-[19px] mb-3" style={{ fontWeight: 500 }}>
          {r.year}
        </div>
        {r.studies && r.studies.length > 0 && (
          <ul className="space-y-1.5 font-serif-body text-[15px] leading-[1.7] text-[#3a466e] list-none">
            {r.studies.map((s) => (
              <li key={s} className="flex items-start gap-3">
                <Diamond />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        )}
        {r.books && r.books.length > 0 && (
          <BookList visible={r.books} hidden={r.moreBooks} />
        )}
      </div>
    ))}
  </div>
);

const LevelBlock = ({ lvl }) => (
  <div className="py-7 md:py-9 first:pt-0 md:first:pt-0">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
      <div className="md:col-span-4">
        <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
          Grade / Level
        </div>
        <div className="font-display text-[#1e2952] text-[20px] md:text-[22px] leading-snug" style={{ fontWeight: 600 }}>
          {lvl.grade}
        </div>
      </div>

      <div className="md:col-span-8">
        {lvl.resources && lvl.resources.length > 0 && (
          <>
            <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
              Curriculum Resource
            </div>
            <ul className="space-y-1.5 font-serif-body text-[16px] leading-[1.7] text-[#3a466e] list-none mb-5">
              {lvl.resources.map((r) => (
                <ResourceListItem key={typeof r === "object" ? r.name : r} resource={r} />
              ))}
            </ul>
          </>
        )}

        {lvl.description && (
          <p className="font-serif-body italic text-[15.5px] md:text-[16px] leading-[1.75] text-[#3a466e] mb-5">
            {lvl.description}
          </p>
        )}

        {lvl.focus && lvl.focus.length > 0 && (
          <>
            <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
              Course of Study
            </div>
            <ul className="space-y-1.5 font-serif-body text-[15.5px] leading-[1.7] text-[#3a466e] list-none">
              {lvl.focus.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Diamond />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {lvl.rotation && lvl.rotation.length > 0 && <LevelRotation rotation={lvl.rotation} />}

        {lvl.books && lvl.books.length > 0 && (
          <BookList visible={lvl.books} hidden={lvl.moreBooks} />
        )}
      </div>
    </div>
  </div>
);

const LevelsPanel = ({ section }) => (
  <div className="pt-4 md:pt-6 pb-10 md:pb-12">
    <div className="divide-y divide-[#eae1c4]">
      {section.levels.map((lvl) => (
        <LevelBlock key={lvl.grade} lvl={lvl} />
      ))}
    </div>

    {section.trailingProgression && (
      <div className="pt-8 mt-4 border-t border-[#eae1c4]">
        <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-4 text-center">
          {section.trailingProgression.label}
        </div>
        <div className="flex flex-col md:flex-row md:flex-wrap items-center md:justify-center gap-y-2 md:gap-y-2 md:gap-x-4">
          {section.trailingProgression.steps.map((s, i) => (
            <React.Fragment key={s}>
              <span className="font-display italic text-[#1e2952] text-[18px] md:text-[20px]">{s}</span>
              {i < section.trailingProgression.steps.length - 1 && (
                <span className="text-[#c9a961]">
                  <span className="md:hidden">↓</span>
                  <span className="hidden md:inline">→</span>
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    )}

    {section.footer && (
      <div className="mt-10 pt-6 border-t border-[#eae1c4]">
        <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
          {section.footer.eyebrow}
        </div>
        <p className="font-serif-body italic text-[#3a466e] text-[16px] md:text-[17px] leading-[1.75]">
          {section.footer.body}
        </p>
      </div>
    )}
  </div>
);

/* --------------------------------------------------------------------- */
/* Dispatcher: pick the right panel variant for this section              */
/* --------------------------------------------------------------------- */

const SubjectPanel = ({ section }) => {
  if (section.humanitiesForm) return <HumanitiesFormPanel section={section} />;
  if (section.rotationYears && section.rotationYears.length > 0) return <RotationYearsPanel section={section} />;
  if (section.sampleBooklist && section.sampleBooklist.length > 0) return <SampleBooklistPanel section={section} />;
  if (section.subjects && section.subjects.length > 0) return <SubjectsPanel section={section} />;
  if ((section.progression && section.progression.length > 0) || section.resource || section.progressionNote) {
    return <ProgressionPanel section={section} />;
  }
  return <LevelsPanel section={section} />;
};

/* --------------------------------------------------------------------- */
/* One accordion row                                                      */
/* --------------------------------------------------------------------- */

const AccordionRow = ({ section, isFirst }) => {
  const [open, setOpen] = useState(false);
  const panelId = `curriculum-panel-${section.id}`;
  const buttonId = `curriculum-trigger-${section.id}`;
  const toggle = useCallback(() => setOpen((v) => !v), []);

  return (
    <div
      className={`border-b border-[#c9a961]/60 ${isFirst ? "border-t border-[#c9a961]/60" : ""}`}
      data-testid={`curriculum-row-${section.id}`}
    >
      <h3 className="m-0">
        <button
          id={buttonId}
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={panelId}
          className="w-full flex items-center justify-between text-left gap-6 py-6 md:py-7 px-1 md:px-2 group focus:outline-none focus:ring-2 focus:ring-[#c9a961] focus:ring-offset-2 focus:ring-offset-[#fbf7ee]"
          data-testid={`curriculum-trigger-${section.id}`}
        >
          <span className="flex-1">
            {section.eyebrow && (
              <span className="block font-sans-nav text-[10.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-1.5">
                {section.eyebrow}
              </span>
            )}
            <span className="block font-display text-[#1e2952] text-[22px] md:text-[26px] lg:text-[28px] leading-tight" style={{ fontWeight: 500 }}>
              {section.title}
            </span>
            {section.meta && (
              <span className="block font-sans-nav text-[10.5px] tracking-[0.28em] uppercase text-[#8a7a4e] mt-1.5">
                {section.meta}
              </span>
            )}
          </span>
          <span
            aria-hidden="true"
            className="shrink-0 flex items-center justify-center w-8 h-8 md:w-9 md:h-9 border border-[#c9a961] text-[#c9a961] group-hover:bg-[#c9a961] group-hover:text-[#fbf7ee] transition-colors"
          >
            {open ? <Minus size={16} strokeWidth={1.75} /> : <Plus size={16} strokeWidth={1.75} />}
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        data-testid={`curriculum-panel-${section.id}`}
      >
        {open && <SubjectPanel section={section} />}
      </div>
    </div>
  );
};

/* --------------------------------------------------------------------- */
/* Public component                                                       */
/* --------------------------------------------------------------------- */

const CurriculumAccordion = ({ sections, className = "max-w-[1100px] mx-auto" }) => {
  return (
    <div className={className} data-testid="curriculum-accordion">
      {sections.map((s, i) => (
        <AccordionRow key={s.id} section={s} isFirst={i === 0} />
      ))}
    </div>
  );
};

export default CurriculumAccordion;
